import {
  Button,
  createStyles,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useState} from 'react';

import {
  publishAnalysisTemplate,
  UpdateAnalysisTemplateMetadataParams,
} from 'analysis-tool/templates/AnalysisTemplateActions';
import * as Analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/analytics-products/AnalysisToolAnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';

import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';
import {useAnalysisTemplateDescriptionState} from './useAnalysisTemplateDescriptionState';
import {useAnalysisTemplateNameState} from './useAnalysisTemplateNameState';

export type PublishAnalysisTemplateModalProps = RunModalProps<void> & {
  analysisTemplate: AnalysisTemplateGraphQL;
};

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));

function getPublishDisabledReason(
  analysisTemplate: AnalysisTemplateGraphQL,
  nameError: string | null,
  descriptionError: string | null
): string | null {
  const hasNoSteps = analysisTemplate.steps.length === 0;
  const hasUnconfiguredSteps = analysisTemplate.steps.some(
    (step) =>
      step.config == null &&
      Object.keys(getStepTypeConfigShape(step.stepType, step.stepVersion).fields).length > 0
  );
  if (hasNoSteps) {
    return 'Template must contain at least one step before publishing.';
  }
  if (hasUnconfiguredSteps) {
    return 'All template steps must be configured before publishing.';
  }
  return nameError ?? descriptionError;
}

export default function PublishAnalysisTemplateModal({
  analysisTemplate,
  close,
}: PublishAnalysisTemplateModalProps): JSX.Element {
  const {name, trimmedName, isNameBlank, nameError, setName} = useAnalysisTemplateNameState(
    analysisTemplate.name
  );
  const [templateCollectionId, setTemplateCollectionId] = useState(analysisTemplate.templateCollection.id);
  const {
    description,
    normalizedDescription,
    isDescriptionTooLong,
    descriptionHelpText,
    descriptionError,
    setDescription,
  } = useAnalysisTemplateDescriptionState(analysisTemplate.description);
  const [isSaving, setIsSaving] = useState(false);

  const {classes} = useStyles();

  const movable = analysisTemplate.permissions.movable ?? false;

  const submitDisabledReasonId = useId();
  const submitDisabledTooltip = getPublishDisabledReason(analysisTemplate, nameError, descriptionError);
  const submitDisabled = submitDisabledTooltip != null;

  async function handleSave(): Promise<void> {
    const params: UpdateAnalysisTemplateMetadataParams = {};
    if (trimmedName !== analysisTemplate.name) {
      params.name = trimmedName;
    }
    if (templateCollectionId !== analysisTemplate.templateCollection.id) {
      params.templateCollectionId = templateCollectionId;
    }

    if (normalizedDescription !== analysisTemplate.description) {
      params.description = normalizedDescription;
    }

    setIsSaving(true);
    try {
      await publishAnalysisTemplate(analysisTemplate, params);

      Analytics.trackEventWithModule(
        ANALYSIS_TOOL_ANALYTICS_ACTIONS.PUBLISHED_ANALYSIS_TEMPLATE,
        ANALYTICS_MODULE.ANALYSIS_TOOL,
        {
          actionOrigin: ActionOrigin.ANALYSIS_TOOL_HEADER,
          analysisTemplateId: analysisTemplate.id,
        }
      );
      close();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'PublishAnalysisTemplateModal', element: 'root'})}>
      <SimpleGrid cols={2} spacing="md">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
          error={isNameBlank}
          data-autofocus={true}
        />
        <Stack spacing="xs">
          <Text size="sm" weight={600}>
            Template collection
          </Text>
          {movable ? (
            <TemplateCollectionSelector
              searchable={true}
              value={templateCollectionId}
              onChange={(templateCollection) => {
                setTemplateCollectionId(templateCollection.id);
              }}
            />
          ) : (
            <Text>{analysisTemplate.templateCollection.name}</Text>
          )}
        </Stack>
      </SimpleGrid>
      <Textarea
        classNames={{input: classes.input}}
        inputWrapperOrder={['label', 'input', 'description']}
        label="Description"
        description={descriptionHelpText}
        error={isDescriptionTooLong}
        value={description ?? ''}
        onChange={(e) => {
          setDescription(e.currentTarget.value);
        }}
      />
      <ModalFooter>
        <Group position="right">
          <Tooltip
            // ButtonWithDisabledReason's tooltip cannot adjust its z-index, so it would render behind the modal.
            events={{focus: true, hover: true, touch: false}}
            label={submitDisabledTooltip ?? ''}
            disabled={!submitDisabled}
            withinPortal={true}
            zIndex={ZINDEX_FLOATING_UI}
          >
            <Button
              loading={isSaving}
              aria-describedby={submitDisabled ? submitDisabledReasonId : undefined}
              {...getDisabledTooltipButtonProps(submitDisabled, handleSave, true)}
              {...getTestProps({component: 'PublishAnalysisTemplateModal', element: 'publishButton'})}
            >
              Publish
            </Button>
          </Tooltip>
          {submitDisabled && (
            <span className={classes.visuallyHidden} id={submitDisabledReasonId}>
              {submitDisabledTooltip}
            </span>
          )}
        </Group>
      </ModalFooter>
    </Stack>
  );
}
